import { useState, useEffect, useRef } from "react";

export async function updateGrantQuestions(grantID, newQuestions) {
  const response = await fetch(`${process.env.REACT_APP_API_URL}/api/update-grant-content/${grantID}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      questions: newQuestions,
    }),
  });

  if (!response.ok) {
    throw new Error("Failed to save changes");
  }
}

export function useFetchGrantData(grantID) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!grantID) return;

    let intervalId;
    let isInitialFetch = true;

    async function fetchData() {
      try {
        if (isInitialFetch) setLoading(true);
        setError(null);
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/get-grant-content/${grantID}`);
        if (!response.ok) throw new Error("Failed to fetch");
        
        const result = await response.json();
        const questions = result.content || [];

        setData(questions);

        // If we found the questions, stop polling!
        if (questions.length > 0 && intervalId) {
          clearInterval(intervalId);
        }

        return questions.length > 0; // Return true if we found the data
      } catch (err) {
        console.error(err);
        if (isInitialFetch) setError("Could not load questions.");
        return false;
      } finally {
        setLoading(false);
        isInitialFetch = false;
      }
    }

    // Wait for the first fetch to finish before deciding if we need to poll
    fetchData().then((hasQuestions) => {
      if (!hasQuestions) {
        // Start polling every 15 seconds ONLY if we don't have the questions yet
    intervalId = setInterval(fetchData, 15000);
      }
    });

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [grantID]);

    return { data, loading, error, setData };
}

function countWriterAnswers(allWriterAnswers) {
  return Object.values(allWriterAnswers).reduce((total, questionAnswers) => {
    if (Array.isArray(questionAnswers)) {
      return total + questionAnswers.filter(Boolean).length;
    }

    if (questionAnswers && typeof questionAnswers === "object") {
      return total + Object.values(questionAnswers).filter(Boolean).length;
    }

    return total + (questionAnswers ? 1 : 0);
  }, 0);
}

export function useFetchWriterAnswers(uid, clientID, grantID, shouldStartPolling) {
  const [answers, setAnswers] = useState({});
  const answersRef = useRef({});
  const expectedAnswersRef = useRef(null);

    // Answers and their expected count belong to a specific writer/client/grant combination.
  useEffect(() => {
    answersRef.current = {};
    expectedAnswersRef.current = null;
    setAnswers({});
  }, [uid, clientID, grantID]);

  useEffect(() => {
    if (!uid || !clientID || !grantID) return;

    let intervalId;
    let isActive = true;

    async function fetchAnswers() {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/get-writer-answers/${uid}/${clientID}/${grantID}`);
        if (!response.ok) throw new Error("Failed to fetch answers");

        const result = await response.json();
        if (!isActive) return false;

                const fetched = result.all_writer_answers;
        const fetchedAnswers = fetched && typeof fetched === "object" ? fetched : {};
        const incomingExpectedAnswers = Number(result.expected_answers);

        if (Number.isInteger(incomingExpectedAnswers) && incomingExpectedAnswers >= 0) {
          expectedAnswersRef.current = incomingExpectedAnswers;
        }

        // Some polling responses contain only newly completed writers. Merge at the
        // question and writer levels so a new writer cannot replace existing full text.
        const mergedAnswers = { ...answersRef.current };
        Object.entries(fetchedAnswers).forEach(([questionNumber, incomingAnswer]) => {
          const existingAnswer = mergedAnswers[questionNumber];
          const isWriterAnswerMap = incomingAnswer && typeof incomingAnswer === "object" && !Array.isArray(incomingAnswer);
          const hasExistingWriterAnswerMap = existingAnswer && typeof existingAnswer === "object" && !Array.isArray(existingAnswer);

          mergedAnswers[questionNumber] = isWriterAnswerMap
            ? {
                ...(hasExistingWriterAnswerMap ? existingAnswer : {}),
                ...incomingAnswer,
              }
            : incomingAnswer;
        });

        answersRef.current = mergedAnswers;
        setAnswers(mergedAnswers);

                const currentAnswerCount = countWriterAnswers(mergedAnswers);
        const expectedAnswerCount = expectedAnswersRef.current;
        const isComplete = expectedAnswerCount !== null && currentAnswerCount >= expectedAnswerCount;

        if (isComplete && intervalId) {
          clearInterval(intervalId);
        }

        return isComplete;
      } catch (err) {
        console.error("Could not load writer answers:", err);
        return false;
      }
    }

    fetchAnswers().then((isComplete) => {
      if (isActive && !isComplete && shouldStartPolling) {
        intervalId = setInterval(fetchAnswers, 20000);
      }
    });

    return () => {
      isActive = false;
      if (intervalId) clearInterval(intervalId);
    };
  }, [uid, clientID, grantID, shouldStartPolling]);

  return answers;
}

